/**
 * Timezone utility functions for US timezones only
 */

import { useAuthStore } from '@/stores/auth'

export interface TimezoneOption {
  value: string
  label: string
}

/**
 * US timezone options (same as notes)
 */
export const US_TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' }
]

/**
 * Detect user's timezone using browser API
 * Returns a US timezone if detected, or null if not a US timezone
 */
export function detectUserTimezone(): string | null {
  try {
    const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Check if detected timezone is in our US timezones list
    const isUSTimezone = US_TIMEZONE_OPTIONS.some(opt => opt.value === detectedTz)
    
    if (isUSTimezone) {
      return detectedTz
    }
    
    // If not a US timezone, try to map common timezone patterns to US timezones
    // This handles cases where browser might return a different format
    const timezoneMap: Record<string, string> = {
      'America/New_York': 'America/New_York',
      'America/Detroit': 'America/New_York',
      'America/Indianapolis': 'America/New_York',
      'America/Chicago': 'America/Chicago',
      'America/Denver': 'America/Denver',
      'America/Phoenix': 'America/Phoenix',
      'America/Los_Angeles': 'America/Los_Angeles',
      'America/Anchorage': 'America/Anchorage',
      'Pacific/Honolulu': 'Pacific/Honolulu',
    }
    
    // Try exact match first
    if (timezoneMap[detectedTz]) {
      return timezoneMap[detectedTz]
    }
    
    // Try partial match (e.g., "America/New_York" matches "America/New_York")
    for (const [key, value] of Object.entries(timezoneMap)) {
      if (detectedTz.includes(key) || key.includes(detectedTz)) {
        return value
      }
    }
    
    // Default to Eastern Time if we can't determine
    return 'America/New_York'
  } catch (error) {
    console.warn('Failed to detect timezone:', error)
    // Default to Eastern Time on error
    return 'America/New_York'
  }
}

/**
 * Get timezone options for dropdown
 */
export function getTimezoneOptions(): TimezoneOption[] {
  return US_TIMEZONE_OPTIONS
}

/**
 * Get display label for a timezone
 */
export function getTimezoneLabel(timezone: string | null | undefined): string {
  if (!timezone) return 'Eastern Time (ET)'
  const option = US_TIMEZONE_OPTIONS.find(opt => opt.value === timezone)
  return option ? option.label : timezone
}

/**
 * Check if a timezone is valid (in our US timezones list)
 */
export function isValidTimezone(timezone: string | null | undefined): boolean {
  if (!timezone) return false
  return US_TIMEZONE_OPTIONS.some(opt => opt.value === timezone)
}

/**
 * Get current user's timezone from auth store
 * Returns the user's saved timezone preference or defaults to 'America/New_York'
 */
export function getUserTimezone(): string {
  const authStore = useAuthStore()
  return authStore.user?.timezone || 'America/New_York'
}

